import { SVGProps } from 'react';
import { cn } from '@/lib/utils';

/**
 * Icon · references sprite symbol via <use href="#icon-{name}-{variant}"/>
 * Per Olga §6 Icon System
 *
 * Sizes per design system:
 *   sm  → 16×16 · inline w tekście, micro accents
 *   md  → 20×20 · default (nav, buttons)
 *   lg  → 24×24 · quick actions, large affordances
 *
 * Color inherits from parent CSS (currentColor)
 *
 * Active state pattern (sidebar nav):
 *   <Icon name="dashboard" variant={isActive ? 'filled' : 'outline'} />
 */

const SIZE_MAP = {
  sm: 16,
  md: 20,
  lg: 24,
} as const;

export type IconName =
  // MVP / Navigation (8)
  | 'dashboard' | 'cases' | 'contacts' | 'documents'
  | 'hearings' | 'tasks' | 'invoices' | 'settings'
  // MVP / CRUD Actions (6)
  | 'add' | 'edit' | 'delete' | 'search' | 'filter' | 'upload'
  // MVP / Status (4)
  | 'check' | 'warning' | 'notification' | 'more'
  // Phase 2 / Legal (6)
  | 'court-hammer' | 'scale' | 'signature' | 'stamp' | 'lock-shield' | 'gavel-mail'
  // Phase 2 / Extra Actions (4)
  | 'download' | 'share' | 'time' | 'tag'
  // Phase 2 / App States (4)
  | 'loading' | 'empty-state' | 'error' | 'success-large'
  // V2 / Sidebar & UI additions (8)
  | 'calendar' | 'inbox' | 'template' | 'ai-research'
  | 'phone' | 'arrow-right' | 'trending-up' | 'deadline';

export type IconVariant = 'outline' | 'filled';
export type IconSize = keyof typeof SIZE_MAP;

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  variant?: IconVariant;
  size?: IconSize | number;
}

export function Icon({
  name,
  variant = 'outline',
  size = 'md',
  className,
  ...props
}: IconProps) {
  const dimension = typeof size === 'number' ? size : SIZE_MAP[size];
  return (
    <svg
      width={dimension}
      height={dimension}
      className={cn('inline-block flex-shrink-0', className)}
      aria-hidden="true"
      {...props}
    >
      <use href={`#icon-${name}-${variant}`} />
    </svg>
  );
}

export default Icon;
